import { addDays, format } from 'date-fns';

import { PRESET_DATES_HELPERS } from 'lib/dateUtilities';
import { createSelector } from 'reselect';
import { icons } from 'images';

const facebookPageLikes = (state) => state.crmSocial.facebookLikesAndPageViews;
const facebookReactionsCount = (state) => state.crmSocial.facebookPosts || [];
const facebookPageImpressions = (state) => state.crmSocial.facebookPageImpressions || [];
const facebookPageVideoViews = (state) => state.crmSocial.facebookPageVideoViews || [];
const facebookPageSourceData = (state) => state.crmSocial.facebookPageSourceData || [];
const facebookDemographicsData = (state) => state.crmSocial.facebookDemographicsData || [];
const facebookLocationData = (state) => state.crmSocial.facebookLocationData || [];
const facebookPagePaidNonPaidData = (state) => state.crmSocial.facebookPagePaidNonPaidData || [];
const facebookPageLikeCount = (state) => state.crmSocial.facebookPageLikeCount;
const facebookViewSocialSources = (state) => state.crmAnalytics.analytics.social_sources;

export const dateTrackerTemporarySolution = {
  dates: {
    startDate: format(new Date(PRESET_DATES_HELPERS.startOfLastMonth), 'yyyy-MM-dd'),
    endDate: format(new Date(PRESET_DATES_HELPERS.endOfLastMonth), 'yyyy-MM-dd'),
    prevStart: format(new Date(PRESET_DATES_HELPERS.startOfMonthBeforeLast), 'yyyy-MM-dd'),
    prevEnd: format(new Date(PRESET_DATES_HELPERS.endOfMonthBeforeLast), 'yyyy-MM-dd'),
  },
};

export const facebookStatsBarSelector = createSelector(facebookPageLikes, facebookReactionsCount, facebookPageVideoViews, facebookPageImpressions, facebookPageLikeCount, (fbPageLikes, fbReactionsCount, fbVideoViews, fbImpressions, facebookPageLikeCount) => {
  const { startDate } = dateTrackerTemporarySolution.dates;
  const currentMonthName = format(addDays(new Date(startDate), 5), 'MMM');
  const fbReactionsCountFormatted = (fbReactionsCount || [])
    .map((item) => (item.insights ? item.insights.data : []))
    .flat()
    .filter((item) => item.name === 'post_reactions_by_type_total')
    .map((item) => item.values)
    .flat()
    .reduce((all, item) => {
      return (all += item.value.like ? item.value.like : 0);
    }, 0);

  const totalPageImpressions = (fbImpressions || []).filter((item) => item.end_time_formatted === currentMonthName).reduce((all, item) => (all += item.value), 0);

  const totalVideoViews = (fbVideoViews || []).filter((item) => item.end_time_formatted === currentMonthName).reduce((all, item) => (all += item.value), 0);

  return [
    {
      name: 'Total Likes',
      isPlural: true,
      value: facebookPageLikeCount ? facebookPageLikeCount.toLocaleString() : '...',
      icon: icons['icon-social-likes'],
      isLive: true,
    },
    // {
    //   name: 'Likes',
    //   isPlural: true,
    //   value: likesCalculated ? likesCalculated : '...',
    //   icon: icons['icon-social-likes'],
    // },
    {
      name: 'Reactions',
      isPlural: true,
      value: fbReactionsCountFormatted ? fbReactionsCountFormatted.toLocaleString() : '...',
      icon: icons['icon-social-engagement'],
    },
    {
      name: 'Video Views',
      isPlural: true,
      value: totalVideoViews ? totalVideoViews.toLocaleString() : '...',
      icon: icons['icon-social-reach'],
    },
    {
      name: 'Impressions',
      isPlural: true,
      value: totalPageImpressions ? totalPageImpressions.toLocaleString() : '...',
      icon: icons['icon-social-reach'],
    },
  ];
});

export const pageImpressionsThreeMonthsSelector = createSelector(facebookPageImpressions, (pageImpressions) => {
  const valueMap = {};
  return (pageImpressions || []).reduce((all, item) => {
    if (!valueMap[item.end_time_formatted]) {
      valueMap[item.end_time_formatted] = 0;
    }
    valueMap[item.end_time_formatted] += item.value;
    return Object.entries(valueMap).reduce((all, item) => {
      all.push({ Impressions: item[1], month: item[0] });
      return all;
    }, []);
  }, []);
});

export const videoViewsThreeMonthsSelector = createSelector(facebookPageVideoViews, (pageVideoViews) => {
  const valueMap = {};
  return (pageVideoViews || []).reduce((all, item) => {
    if (!valueMap[item.end_time_formatted]) {
      valueMap[item.end_time_formatted] = 0;
    }
    valueMap[item.end_time_formatted] += item.value;
    return Object.entries(valueMap).reduce((all, item) => {
      all.push({ Views: item[1], month: item[0] });
      return all;
    }, []);
  }, []);
});

export const facebookPageLikesBySourceSelector = createSelector(facebookPageSourceData, (pageSourceData) => {
  const valuelessItemsFilteredAndFormatted = (pageSourceData || [])
    .filter((n) => Object.keys(n.value).length > 0)
    .map((item) => {
      return { ...item, value: Object.entries(item.value) };
    });
  let temp = [];
  //Populate temp
  valuelessItemsFilteredAndFormatted.forEach((item) => {
    temp = [...temp, ...item.value];
  });
  const final = temp.reduce((all, item) => {
    const name = item[0];
    const value = item[1];
    if (!all[name]) {
      all[name] = 0;
    }
    all[name] += value;
    return all;
  }, {});
  return Object.entries(final).map((item) => {
    const name = item[0];
    const value = item[1];
    return { name, value };
  });
});

export const facebookAgeAndGenderDemographicsListData = createSelector(facebookDemographicsData, (demographicData) => {
  const sorted = Object.entries(demographicData).sort((a, b) => b[1] - a[1]);
  const final = Object.entries(sorted)
    .reduce((all, item) => {
      all.push({ label: item[1][0], value: item[1][1] });
      return all;
    }, [])
    .slice(0, 5);
  return final;
});
export const facebookGenderDemographicPercentages = createSelector(facebookDemographicsData, (demographicData) => {
  const accumulatedValues = Object.entries(demographicData).reduce(
    (all, item) => {
      const name = item[0];
      const value = item[1];
      if (!name.includes('U')) {
        all.total += value;
      }
      if (name.includes('F')) {
        all.female += value;
      }
      if (name.includes('M')) {
        all.male += value;
      }
      return all;
    },
    { female: 0, male: 0, total: 0 }
  );
  const { female, male, total } = accumulatedValues;
  return [
    { name: 'Female', value: parseInt(((female / total) * 100).toFixed(0)) },
    { name: 'Males', value: parseInt(((male / total) * 100).toFixed(0)) },
  ];
});

export const facebookLocationDataSelector = createSelector(facebookLocationData, (locationData) => {
  const sorted = Object.entries(locationData).sort((a, b) => b[1] - a[1]);
  const final = Object.entries(sorted).reduce((all, item, i) => {
    all.push({ label: item[1][0], value: item[1][1], ranking: i + 1 });
    return all;
  }, []);
  return (final || []).slice(0, 5);
});

export const facebookPaidOrganicSelector = createSelector(facebookPagePaidNonPaidData, (facebookPagePaidNonPaidData) => {
  const formattedValuesForGraph = (facebookPagePaidNonPaidData || []).reduce((all, item) => {
    if (!all[item.end_time_formatted]) {
      all[item.end_time_formatted] = { Paid: 0, Organic: 0 };
    }
    if (item.value.paid) {
      all[item.end_time_formatted].paid = all[item.end_time_formatted].paid += item.value.paid;
    }
    if (item.value.unpaid) {
      all[item.end_time_formatted].Organic = all[item.end_time_formatted].Organic += item.value.unpaid;
    }
    return all;
  }, {});

  return Object.entries(formattedValuesForGraph).reduce((all, item) => {
    all.push({ name: item[0], Paid: item[1].Paid, Organic: item[1].Organic });
    return all;
  }, []);
});

export const facebookViewSocialSourcesSelector = createSelector(facebookViewSocialSources, (facebookViewSocialSources) => {
  if (facebookViewSocialSources === 'failed') {
    return 'failed';
  }
  return (facebookViewSocialSources || []).map((n) => ({ ...n, [n.label]: n.currentData }));
});
